import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import { api, formatApiError } from "../../lib/api";
import { Badge, Button, Card, Input, Label } from "../../components/ui-kit";
import { Brain, Database, SlidersHorizontal } from "@phosphor-icons/react";

const STATUS_META = {
  active: { label: "Actif", tone: "success" },
  partial: { label: "Partiel", tone: "warning" },
  needs_provider: { label: "Provider requis", tone: "danger" },
  needs_ai: { label: "IA requise", tone: "primary" },
  planned: { label: "Futur", tone: "dark" },
};

const statusOptions = [
  ["all", "Tous statuts"],
  ["active", "Actif"],
  ["partial", "Partiel"],
  ["needs_provider", "Provider requis"],
  ["needs_ai", "IA requise"],
  ["planned", "Futur"],
];

export default function AdminPlatform() {
  const [data, setData] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [section, setSection] = useState("all");
  const [editing, setEditing] = useState("");
  const [drafts, setDrafts] = useState({});

  const load = () => {
    api.get("/admin/platform/capabilities")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/system/status")
      .then(({ data }) => setSystemStatus(data))
      .catch(() => setSystemStatus(null));
  };

  useEffect(() => { load(); }, []);

  const sections = useMemo(() => data?.groups || [], [data?.groups]);
  const flatItems = useMemo(() => sections.flatMap((group) => group.items.map((item) => ({ ...item, groupTitle: group.title }))), [sections]);
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return flatItems.filter((item) => {
      const matchesQuery = !q || [item.label, item.note, item.admin_note, item.groupTitle]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
      const matchesStatus = status === "all" || item.status === status;
      const matchesSection = section === "all" || item.section_key === section;
      return matchesQuery && matchesStatus && matchesSection;
    });
  }, [flatItems, query, section, status]);

  const saveCapability = async (item) => {
    const draft = drafts[item.key] || {};
    setEditing(item.key);
    try {
      await api.patch(`/admin/platform/capabilities/${item.key}`, {
        status_override: draft.status_override || item.status,
        admin_note: draft.admin_note ?? item.admin_note ?? "",
      });
      toast.success("Capacite mise a jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditing("");
    }
  };

  const setDraft = (key, patch) => setDrafts((current) => ({ ...current, [key]: { ...current[key], ...patch } }));

  return (
    <AdminShell title="Plateforme">
      <div className="admin-page-header">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-primary">Architecture super-app</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Capacites plateforme</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Tous les domaines Uber-like sont recenses ici: actifs, partiels, providers requis et modules futurs.
          </p>
        </div>
        <Badge tone="primary">{data?.total || 0} capacites</Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <Card key={key} className="p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{meta.label}</div>
            <div className="mt-1 flex items-end justify-between gap-2">
              <div className="font-heading text-3xl font-black">{data?.summary?.[key] || 0}</div>
              <Badge tone={meta.tone}>{key}</Badge>
            </div>
          </Card>
        ))}
      </div>

      <Card className="mt-5 p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-primary">Etat systeme</div>
            <h2 className="font-heading text-2xl font-black tracking-tight">Observabilite, jobs, analytics et securite</h2>
          </div>
          <Button onClick={load} variant="secondary">Actualiser</Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {[
            ["Prometheus", systemStatus?.observability?.prometheus?.configured, systemStatus?.observability?.prometheus?.metrics_endpoint],
            ["Sentry", systemStatus?.observability?.sentry?.configured, "Erreurs frontend/backend"],
            ["OpenTelemetry / Jaeger", systemStatus?.observability?.opentelemetry?.configured, systemStatus?.observability?.jaeger?.local_url],
            ["Redis + Jobs", systemStatus?.jobs?.redis?.up, Object.entries(systemStatus?.jobs?.queues || {}).map(([name, q]) => `${name}:${q.depth ?? "-"}`).join("  ")],
            ["Event Stream", systemStatus?.streaming?.redis_stream?.configured, systemStatus?.streaming?.redis_stream?.stream],
            ["Redpanda", systemStatus?.streaming?.redpanda?.configured, systemStatus?.streaming?.redpanda?.bootstrap],
            ["ClickHouse", systemStatus?.analytics?.clickhouse?.configured, `${systemStatus?.analytics?.mongodb_events || 0} evenements Mongo`],
            ["CI/CD rollback", systemStatus?.cicd?.production_cicd_configured, systemStatus?.cicd?.rollback_script],
            ["Tests E2E", systemStatus?.cicd?.e2e_tests_configured, "deploy/scripts/e2e_smoke.sh"],
            ["Monitoring securite", systemStatus?.security_monitoring?.security_monitoring_configured, "cron + alerts"],
            ["Secrets", systemStatus?.security_monitoring?.secrets_monitoring_configured, "guard GitHub + cron"],
            ["Load balancer", systemStatus?.scaling?.load_balancer_configured, "multi-serveurs"],
          ].map(([label, ok, note]) => (
            <div key={label} className="rounded-2xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-black">{label}</div>
                <Badge tone={ok ? "success" : "warning"}>{ok ? "OK" : "A configurer"}</Badge>
              </div>
              <div className="mt-2 truncate text-xs text-muted-foreground">{note || "-"}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mt-5 p-4">
        <div className="grid gap-3 lg:grid-cols-[1fr_220px_240px]">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une capacite, domaine, note..." />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-white/10 bg-[#15171b] px-3 py-3 text-sm font-bold text-white [color-scheme:dark]">
            {statusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={section} onChange={(e) => setSection(e.target.value)} className="rounded-xl border border-white/10 bg-[#15171b] px-3 py-3 text-sm font-bold text-white [color-scheme:dark]">
            <option value="all">Tous domaines</option>
            {sections.map((group) => <option key={group.key} value={group.key}>{group.title}</option>)}
          </select>
        </div>
      </Card>

      <div className="mt-5 grid gap-4">
        {filteredItems.map((item) => {
          const meta = STATUS_META[item.status] || STATUS_META.planned;
          const draft = drafts[item.key] || {};
          return (
            <Card key={item.key} className="p-4">
              <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                    <Badge tone="dark">{item.groupTitle}</Badge>
                  </div>
                  <h2 className="mt-3 font-heading text-xl font-black tracking-tight">{item.label}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{item.note}</p>
                  {item.admin_note && (
                    <div className="mt-3 rounded-2xl border border-primary/25 bg-primary/10 p-3 text-sm">
                      <span className="font-bold text-primary">Note admin: </span>{item.admin_note}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                  <div className="mb-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    <SlidersHorizontal size={15} weight="bold" /> Controle admin
                  </div>
                  <div className="grid gap-3">
                    <div>
                      <Label>Statut</Label>
                      <select
                        value={draft.status_override || item.status}
                        onChange={(e) => setDraft(item.key, { status_override: e.target.value })}
                        className="w-full rounded-xl border border-white/10 bg-[#15171b] px-3 py-3 text-sm font-bold text-white [color-scheme:dark]"
                      >
                        {statusOptions.filter(([value]) => value !== "all").map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Note</Label>
                      <textarea
                        value={draft.admin_note ?? item.admin_note ?? ""}
                        onChange={(e) => setDraft(item.key, { admin_note: e.target.value })}
                        className="min-h-20 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-primary"
                        placeholder="Decision, provider, prochaine etape..."
                      />
                    </div>
                    <Button onClick={() => saveCapability(item)} disabled={editing === item.key}>
                      {editing === item.key ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
        {filteredItems.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            Aucun module ne correspond aux filtres.
          </Card>
        )}
      </div>

      <Card className="mt-5 p-4">
        <div className="flex items-start gap-3">
          <span className="rounded-2xl bg-primary/15 p-3 text-primary"><Brain size={22} weight="duotone" /></span>
          <div>
            <h2 className="font-heading text-xl font-black">Ordre conseille</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Avant d'ajouter M3/Peloton/AresDB, stabiliser OpenTelemetry + Jaeger, Prometheus/Grafana, Sentry, logs centralises, puis automatiser fraude, forecasting et dispatch avance.
            </p>
          </div>
          <span className="ml-auto hidden rounded-2xl bg-white/5 p-3 text-muted-foreground sm:block"><Database size={22} weight="duotone" /></span>
        </div>
      </Card>
    </AdminShell>
  );
}
